import React, { PropsWithChildren, useContext, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';

import { generateZeroRatingProps } from './utils/catpcha.utils';

export interface CaptchaProps {
  getToken: () => Promise<string | null>;
}

const CaptchaContext = React.createContext<CaptchaProps>({
  getToken: () => null,
});

type CaptchaProviderProps = PropsWithChildren<{
  hCaptchaKey: string;
  id?: string;
  size?: 'normal' | 'compact' | 'invisible';
  reCaptchaCompat?: boolean;
  isZeroRatingEnabled?: boolean;
}>;

function CaptchaProvider({
  children,
  hCaptchaKey,
  id = 'global-captcha',
  size = 'invisible',
  reCaptchaCompat = false,
  isZeroRatingEnabled = false,
}: CaptchaProviderProps) {
  const captchaRef = useRef<HCaptcha>(null);
  const isReady = useRef<boolean>(false);

  const zeroRatingProps = generateZeroRatingProps(isZeroRatingEnabled);

  const getToken = async () => {
    if (isReady.current) {
      try {
        const { response } = await captchaRef.current?.execute({ async: true });
        return response;
      } finally {
        captchaRef?.current?.resetCaptcha();
      }
    }

    return null;
  };

  return (
    <CaptchaContext.Provider value={{ getToken }}>
      {children}

      <HCaptcha
        id={id}
        size={size}
        sitekey={hCaptchaKey}
        ref={captchaRef}
        reCaptchaCompat={reCaptchaCompat}
        onLoad={() => {
          isReady.current = true;
        }}
        sentry={false} // To avoid scope.getPropagationContext is not a function. https://github.com/hCaptcha/react-hcaptcha/blob/master/src/index.js
        {...zeroRatingProps}
      />
    </CaptchaContext.Provider>
  );
}

export default CaptchaProvider;

export function useCaptcha() {
  const context = useContext(CaptchaContext);
  if (!context) {
    throw new Error('useCaptcha should be used inside CaptchaProvider');
  }
  return context;
}
